import { useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { StatusBadge } from '@/components/ui/Badge';

// Friendly "X ago" for activity timestamps. Falls back to date if older than a week.
function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const min  = Math.round(diffMs / 60_000);
  if (min < 1)    return 'just now';
  if (min < 60)   return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)    return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7)    return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsFor(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
}

export default function Dashboard() {
  const { stats, todayStops, custById, team, customers, stops, invoices, setScreen, openModal, userEmail } = useApp();

  const activeTeam = useMemo(() => team.filter(t => t.active !== false), [team]);

  // Build activity feed from real state: recent paid invoices, completed stops
  // with chem logs, and newly-added customers. Most recent first.
  const activity = useMemo(() => {
    const items = [];
    invoices.forEach(i => {
      if (i.status === 'paid' && i.paidDate) {
        const c = custById(i.custId);
        items.push({
          when: i.paidDate,
          dot:  'var(--teal)',
          title: `Invoice paid — $${(i.amount || 0).toLocaleString()}`,
          sub:   c?.name || i.service || 'Customer',
        });
      }
    });
    stops.forEach(s => {
      if (s.status === 'done') {
        const c = custById(s.custId);
        if (s.chem) {
          items.push({
            when: s.date,
            dot:  'var(--amber)',
            title: 'Chemistry logged',
            sub:   `${c?.name || 'Stop'} · pH ${s.chem.ph ?? '—'}`,
          });
        }
        items.push({
          when: s.date,
          dot:  'var(--cyan)',
          title: 'Stop completed',
          sub:   c?.name || 'Customer',
        });
      }
    });
    customers.forEach(c => {
      if (c.since) {
        items.push({
          when: c.since,
          dot:  'var(--violet)',
          title: 'Customer added',
          sub:   c.name,
        });
      }
    });
    items.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
    return items.slice(0, 5);
  }, [invoices, stops, customers, custById]);

  const overdueAccounts = useMemo(
    () => new Set(invoices.filter(i => i.status === 'overdue').map(i => i.custId)).size,
    [invoices]
  );

  const techStats = useMemo(() => {
    const stat = {};
    team.forEach(t => {
      const myCusts = customers.filter(c => c.tech === t.name);
      const myStops = stops.filter(s => s.tech === t.name);
      const myDone  = myStops.filter(s => s.status === 'done');
      const photoStops = myDone.filter(s => Array.isArray(s.photos) && s.photos.length > 0);
      stat[t.id] = {
        pools:     myCusts.length,
        rating:    t.rating || 5.0,
        photoRate: myDone.length ? Math.round((photoStops.length / myDone.length) * 100) : 100,
        done:      myStops.length ? Math.round((myDone.length / myStops.length) * 100) : 100,
      };
    });
    return stat;
  }, [team, customers, stops]);

  const greetingName = useMemo(() => {
    if (!userEmail) return 'there';
    const local = userEmail.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1);
  }, [userEmail]);

  return (
    <div className="page fade-up">
      {/* Page header */}
      <div className="ph">
        <div>
          <div className="ptitle">Welcome, {greetingName}</div>
          <div className="psub">
            {stats.done} of {stats.total} stops complete · {activeTeam.length} tech{activeTeam.length === 1 ? '' : 's'} active
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('export', { entity: 'customers' })}>Export</button>
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add_stop')}>
            + Add Stop
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="g4">
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--cyan),transparent)' }} />
          <div className="kpi-icon">💰</div>
          <div className="kpi-label">Monthly Revenue</div>
          <div className="kpi-value">${stats.mrr.toLocaleString()}</div>
          <div className="kpi-sub">Across all accounts</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--teal),transparent)' }} />
          <div className="kpi-icon">🏊</div>
          <div className="kpi-label">Stops Today</div>
          <div className="kpi-value">{stats.done}/{stats.total}</div>
          <div className="kpi-sub">{activeTeam.length} tech{activeTeam.length === 1 ? '' : 's'} on route</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--rose),transparent)' }} />
          <div className="kpi-icon">⚠️</div>
          <div className="kpi-label">Overdue Invoices</div>
          <div className="kpi-value">${stats.overdue.toLocaleString()}</div>
          <div className="kpi-sub">
            {overdueAccounts === 0
              ? 'All accounts current'
              : `${overdueAccounts} account${overdueAccounts === 1 ? '' : 's'} past due`}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--violet),transparent)' }} />
          <div className="kpi-icon">👤</div>
          <div className="kpi-label">Active Customers</div>
          <div className="kpi-value">{stats.totalCustomers}</div>
          <div className="kpi-sub">
            {stats.totalCustomers === 0
              ? 'No customers yet'
              : `${stats.totalCustomers} pool${stats.totalCustomers === 1 ? '' : 's'} managed`}
          </div>
        </div>
      </div>

      {/* Three-column grid: stops | team | activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 280px', gap: 12 }}>
        {/* Today's stops */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Today's Stops</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setScreen('routes')}>
              View All →
            </button>
          </div>
          <div className="card-body-0">
            {todayStops.map((stop, i) => {
              const c = custById(stop.custId);
              const numStyle = stop.status === 'done' ? 'sn-done' : stop.status === 'active' ? 'sn-active' : 'sn-next';
              const numLabel = stop.status === 'done' ? '✓' : stop.status === 'active' ? '▶' : i + 1;
              return (
                <div key={stop.id} className="stop-row" onClick={() => setScreen('routes')}>
                  <div className={`stop-num ${numStyle}`}>{numLabel}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{c?.name || ''}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)' }}>{c?.addr || ''} · {stop.tech}</div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--t3)', marginRight: 6 }}>{stop.time}</span>
                  <StatusBadge status={stop.status} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Tech status */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Tech Status — Live</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setScreen('team')}>Team →</button>
          </div>
          <div className="card-body">
            {activeTeam.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 10 }}>
                  No technicians yet
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setScreen('team')}>
                  + Add Technician
                </button>
              </div>
            ) : activeTeam.map(t => {
              const s = techStats[t.id] || { pools: 0, rating: 5.0, photoRate: 100, done: 100 };
              const initials = t.initials || initialsFor(t.name);
              return (
                <div key={t.id} className="tech-card" style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div
                      className="tech-av"
                      style={{ background: `linear-gradient(135deg,${t.color},var(--teal))` }}
                    >
                      {initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--t3)' }}>Active · {s.pools} pool{s.pools === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    {[['Rating', `${s.rating}★`], ['Photos', `${s.photoRate}%`], ['Done', `${s.done}%`]].map(([l, v]) => (
                      <div key={l} style={{ background: 'var(--s2)', borderRadius: 7, padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{v}</div>
                        <div style={{ fontSize: 9.5, color: 'var(--t3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity feed */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Activity Feed</span>
          </div>
          <div className="card-body-0">
            {activity.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--t3)', fontSize: 12 }}>
                No activity yet — paid invoices, completed stops, and new customers will appear here.
              </div>
            ) : activity.map((a, i) => (
              <div key={i} className="tl-item">
                <div className="tl-dot" style={{ background: a.dot }} />
                <div style={{ flex: 1 }}>
                  <div className="tl-title">{a.title}</div>
                  <div className="tl-sub">{a.sub}</div>
                </div>
                <div className="tl-time">{timeAgo(a.when)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
